import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

const GOLD = "#C9A84C";
const DARK = "#1A1510";
const CREAM = "#FAF7F2";
const MUTED = "#8A7D6B";
const BORDER = "#E8E0D4";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Jost:wght@300;400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .ty-root {
    min-height: 100vh;
    background: ${CREAM};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    font-family: 'Jost', sans-serif;
    position: relative;
    overflow: hidden;
  }

  .ty-bg-arc {
    position: absolute;
    top: -200px;
    right: -200px;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    border: 1px solid rgba(201,168,76,0.15);
    pointer-events: none;
  }

  .ty-bg-arc2 {
    position: absolute;
    bottom: -300px;
    left: -100px;
    width: 700px;
    height: 700px;
    border-radius: 50%;
    border: 1px solid rgba(201,168,76,0.10);
    pointer-events: none;
  }

  .ty-card {
    background: white;
    border: 1px solid ${BORDER};
    max-width: 520px;
    width: 100%;
    position: relative;
    animation: fadeUp 0.8s cubic-bezier(0.22,1,0.36,1) both;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ty-header {
    padding: 36px 40px 28px;
    border-bottom: 1px solid ${BORDER};
    position: relative;
    text-align: center;
  }

  .ty-logo-line {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 24px;
  }

  .ty-logo-ornament {
    height: 1px;
    width: 32px;
    background: linear-gradient(to right, transparent, ${GOLD});
  }

  .ty-logo-ornament.right {
    background: linear-gradient(to left, transparent, ${GOLD});
  }

  .ty-brand {
    font-family: 'Cormorant Garamond', serif;
    font-size: 18px;
    font-weight: 400;
    letter-spacing: 0.25em;
    color: ${DARK};
    text-transform: uppercase;
  }

  .ty-check-wrap {
    width: 60px;
    height: 60px;
    margin: 0 auto 20px;
    border: 1px solid rgba(201,168,76,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: popIn 0.5s 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
  }

  @keyframes popIn {
    from { opacity: 0; transform: scale(0.6); }
    to   { opacity: 1; transform: scale(1); }
  }

  .ty-check-svg {
    width: 26px;
    height: 26px;
    stroke: ${GOLD};
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 40;
    stroke-dashoffset: 0;
    animation: drawCheck 0.5s 0.7s ease both;
  }

  @keyframes drawCheck {
    from { stroke-dashoffset: 40; }
    to   { stroke-dashoffset: 0; }
  }

  .ty-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: 30px;
    font-weight: 300;
    color: ${DARK};
    line-height: 1.2;
    margin-bottom: 8px;
  }

  .ty-heading em {
    font-style: italic;
    color: ${GOLD};
  }

  .ty-subtext {
    font-size: 13px;
    color: ${MUTED};
    letter-spacing: 0.04em;
    line-height: 1.6;
  }

  .ty-body {
    padding: 28px 40px 36px;
  }

  .ty-msg {
    font-size: 14px;
    color: ${MUTED};
    line-height: 1.75;
    text-align: center;
    margin-bottom: 28px;
    border-left: 2px solid ${GOLD};
    padding-left: 14px;
    text-align: left;
    font-style: italic;
    font-family: 'Cormorant Garamond', serif;
    font-size: 16px;
  }

  .ty-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }

  .ty-divider-line {
    flex: 1;
    height: 1px;
    background: ${BORDER};
  }

  .ty-divider-dot {
    width: 4px;
    height: 4px;
    background: ${GOLD};
    transform: rotate(45deg);
  }

  .ty-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ty-btn {
    width: 100%;
    padding: 14px 20px;
    border: none;
    cursor: pointer;
    font-family: 'Jost', sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.25s ease;
    position: relative;
    overflow: hidden;
  }

  .ty-btn::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.08);
    opacity: 0;
    transition: opacity 0.2s;
  }

  .ty-btn:hover::after { opacity: 1; }

  .ty-btn-primary {
    background: ${DARK};
    color: ${CREAM};
  }

  .ty-btn-primary:hover {
    background: #2c2218;
    transform: translateY(-1px);
  }

  .ty-btn-outline {
    background: transparent;
    color: ${DARK};
    border: 1px solid ${BORDER};
  }

  .ty-btn-outline:hover {
    border-color: ${GOLD};
    color: ${GOLD};
    transform: translateY(-1px);
  }

  .ty-btn-ghost {
    background: transparent;
    color: ${MUTED};
    font-size: 12px;
    padding: 10px;
    border: none;
  }

  .ty-btn-ghost:hover {
    color: ${DARK};
  }

  .ty-btn-icon {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 1.5;
    fill: none;
    flex-shrink: 0;
  }

  .ty-footer {
    padding: 18px 40px;
    border-top: 1px solid ${BORDER};
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ty-footer-brand {
    font-size: 11px;
    letter-spacing: 0.15em;
    color: ${MUTED};
    text-transform: uppercase;
  }

  .ty-footer-link {
    font-size: 11px;
    color: ${GOLD};
    text-decoration: none;
    letter-spacing: 0.08em;
  }

  .ty-footer-link:hover { text-decoration: underline; }

  .ty-info-row {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .ty-tag {
    font-size: 11px;
    letter-spacing: 0.08em;
    padding: 4px 10px;
    border: 1px solid ${BORDER};
    color: ${MUTED};
    text-transform: uppercase;
    font-family: 'Jost', sans-serif;
  }

  .ty-tag.gold {
    border-color: rgba(201,168,76,0.4);
    color: ${GOLD};
  }
`;

function IconBook() {
  return (
    <svg viewBox="0 0 24 24" className="ty-btn-icon">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
  );
}

function IconGrid() {
  return (
    <svg viewBox="0 0 24 24" className="ty-btn-icon">
      <rect x="3" y="3" width="7" height="7"/>
      <rect x="14" y="3" width="7" height="7"/>
      <rect x="3" y="14" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/>
    </svg>
  );
}

function IconArrow() {
  return (
    <svg viewBox="0 0 24 24" className="ty-btn-icon">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );
}

function ThankYouPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const formData =
    location.state?.formData ||
    JSON.parse(localStorage.getItem("lastFormData") || "{}");

  const [visible, setVisible] = useState(false);
  useEffect(() => { setVisible(true); }, []);

  const handleViewBrochure = () => window.open("/DecodiaBrochure2026.pdf", "_blank");
  const handleViewPortfolio = () => window.open("/Decodia_Interior_Design.pdf", "_blank");
  const handleBackToHome = () => navigate("/");

  const name = formData.name || "Valued Guest";
  const firstName = name.split(" ")[0];

  return (
    <>
      <style>{styles}</style>
      <div className="ty-root">
        <div className="ty-bg-arc" />
        <div className="ty-bg-arc2" />

        <div className="ty-card">
          {/* Header */}
          <div className="ty-header">
            <div className="ty-logo-line">
              <span className="ty-logo-ornament" />
              <span className="ty-brand">Decodia</span>
              <span className="ty-logo-ornament right" />
            </div>

            <div className="ty-check-wrap">
              <svg viewBox="0 0 24 24" className="ty-check-svg">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h1 className="ty-heading">
              Thank you,<br />
              <em>{firstName}.</em>
            </h1>
            <p className="ty-subtext" style={{ marginTop: 8 }}>
              Your enquiry has been received &nbsp;·&nbsp; We'll be in touch shortly
            </p>
          </div>

          {/* Body */}
          <div className="ty-body">
            <p className="ty-msg">
              "Great spaces begin with a conversation. We look forward to crafting
              something extraordinary for you."
            </p>

            <div className="ty-info-row">
              <span className="ty-tag gold">Premium Design</span>
              <span className="ty-tag">Turnkey Interiors</span>
              <span className="ty-tag">Pan India</span>
            </div>

            <div className="ty-divider">
              <span className="ty-divider-line" />
              <span className="ty-divider-dot" />
              <span className="ty-divider-line" />
            </div>

            <div className="ty-actions">
              <button className="ty-btn ty-btn-primary" onClick={handleViewBrochure}>
                <IconBook />
                View Our 2026 Brochure
              </button>

              <button className="ty-btn ty-btn-outline" onClick={handleViewPortfolio}>
                <IconGrid />
                Explore Portfolio
              </button>
  <button className="ty-btn ty-btn-outline">
              <a href="https://decodia.in" className="ty-footer-link">
              Visit Website →
            </a>
            </button>
            </div>
          </div>

          {/* Footer */}
          <div className="ty-footer">
            <span className="ty-footer-brand">Decodia Design Studio</span>
            {/* <a href="https://decodia.in" className="ty-footer-link">
              Visit Website →
            </a> */}
          </div>
        </div>
      </div>
    </>
  );
}

export default ThankYouPage;